// Client for the unified instance metadata REST + WS surface.
// Match state itself stays host-local in sessionStorage; this
// module only talks to the metadata layer.
//
// Spec: docs/superpowers/specs/2026-04-24-unified-instance-save-system-design.md

import { api, connectWebSocket, send, on, off, onServerReconnect } from '../serverClient';

export async function listInstances() {
  return api.get('/instances');
}

export async function listPublicInstances() {
  return api.get('/instances/public');
}

export async function getInstance(instanceId) {
  return api.get(`/instances/${encodeURIComponent(instanceId)}`);
}

export async function createInstance({
  visibility, mode, rankedOptIn, seatsMin, seatsMax, format, creatorDeckId,
  soloGuestDeckId, draftEventId, draft, startingHp, automations, botOpponent,
}) {
  return api.post('/instances', {
    visibility, mode, rankedOptIn, seatsMin, seatsMax, format, creatorDeckId,
    // Solo (one pilot, both seats): the second slot's deck. The server stamps
    // it on the instance and provisions the host===guest room at create time.
    // vs Bot reuses the same field for the bot seat's deck.
    soloGuestDeckId,
    // vs Bot: the server adds a bot participant to the second seat, attaches
    // a botDriver to it, and provisions the room at create time.
    botOpponent,
    // Draft/sealed: pass `draft: { setKey, scheduledAt, paymentMethod }`
    // and the server creates the draft_event and links it in the same
    // request (composite create. No client-crash window between the
    // two writes). `draftEventId` is the legacy two-call form where
    // the client created the event first.
    draftEventId,
    draft,
    // Starting HP. 10 for Quick Match, 20 for Standard. Sent for
    // every mode; the server stores it but only the match runtime
    // consults it. Drafts/sealed ignore the field at runtime.
    startingHp,
    // Session-wide automation mode, picked at setup and shared by every seat.
    // `false` is manual tabletop play: the server resolves nothing on its own.
    // Omitting it entirely reads as `true` server-side (pre-automation-mode
    // clients), so always send the explicit boolean.
    automations,
  });
}

export async function joinInstance(instanceId, { deckId } = {}) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/join`, { deckId });
}

// Pending invitations addressed to the current user. Feeds the
// "Queue & Invites" menu's Pending Invites section.
export async function listInvitedInstances() {
  return api.get('/instances/invited');
}

// Creator-only. kind='join' invites a friend into a gathering lobby;
// kind='resume' pings an existing participant back into a paused
// match. Replaces the old match-invite chat message.
export async function inviteToInstance(instanceId, userId, kind = 'join') {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/invite`, { userId, kind });
}

export async function declineInstanceInvite(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/decline-invite`, {});
}

export async function setInstanceDeck(instanceId, deckId) {
  return api.post(`/instances/${instanceId}/deck`, { deckId });
}

export async function markReady(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/ready`, {});
}

export async function unmarkReady(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/unready`, {});
}

export async function pauseInstance(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/pause`, {});
}

export async function requestResume(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/resume-request`, {});
}

// Creator-only: kicks off the resume ready-check. Server clears
// every participant's readyAt and broadcasts readyCheckStarted so
// the modal opens for every subscriber. Does NOT mark the caller
// ready. They still click through the modal.
export async function resumeStart(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/resume-start`, {});
}

export async function assignRoomCode(instanceId, roomCode) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/room-code`, { roomCode });
}

export async function completeInstance(instanceId, result) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/complete`, { result });
}

export async function abandonInstance(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/abandon`, {});
}

// Non-creator exit. Gathering: frees the seat. Paused/active: concedes
// the match (forfeit win for the opponent). Creators use abandonInstance.
export async function leaveInstance(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/leave`, {});
}

export async function claimWin(instanceId) {
  return api.post(`/instances/${encodeURIComponent(instanceId)}/claim-win`, {});
}

// --- WS subscriptions ---

// The server broadcasts { type: 'instance:event', data: { instanceId, event } }
// to every WS subscribed to that instance. We fan out to per-instance
// handler sets so multiple consumers (SessionBrowser list refresh,
// ReadyCheckModal global overlay, GameBoard in-match tap) can all
// react without stepping on each other.

const handlersByInstance = new Map();
const stateHandlersByInstance = new Map();
let globalListenerAttached = false;

function ensureGlobalListener() {
  if (globalListenerAttached) return;
  globalListenerAttached = true;
  on('instance:event', (data) => {
    if (!data?.instanceId) return;
    const set = handlersByInstance.get(data.instanceId);
    if (!set) return;
    for (const h of set) {
      try { h(data.event); } catch (err) {
        console.error('[instancesApi] handler threw:', err);
      }
    }
  });
  on('instance:state', (data) => {
    if (!data?.instance?.id) return;
    const set = stateHandlersByInstance.get(data.instance.id);
    if (!set) return;
    for (const h of set) {
      try { h(data.instance); } catch (err) {
        console.error('[instancesApi] state handler threw:', err);
      }
    }
  });
  // Server drops every per-instance subscription on ws.close (see
  // ws/instanceChannel.js). Re-send instance:subscribe for every active
  // subscription whenever the socket recovers, otherwise critical
  // one-shot lifecycle events (phaseTransition→active, in particular)
  // silently miss the guest after any drop.
  onServerReconnect(() => {
    for (const instanceId of handlersByInstance.keys()) {
      try { send('instance:subscribe', { instanceId }); } catch {}
    }
  });
}

// Global "the public instance list has changed" channel. The server
// broadcasts this on create/join/abandon/start of any public
// instance. Listeners typically re-fetch listPublicInstances(). No
// per-instance subscribe round trip is needed. The server sends
// this to every connected client via broadcastAll. Returns an
// unsubscribe function.
export function subscribePublicInstancesChanged(handler) {
  on('publicInstances:changed', handler);
  return () => off('publicInstances:changed', handler);
}

// Subscribe to lifecycle events for a single instance. Returns an
// unsubscribe function. Safe to call while the WS is still
// connecting. The server-side subscribe message goes out as soon
// as connectWebSocket resolves.
export async function subscribeInstance(instanceId, handler, onState) {
  ensureGlobalListener();
  if (!handlersByInstance.has(instanceId)) {
    handlersByInstance.set(instanceId, new Set());
  }
  handlersByInstance.get(instanceId).add(handler);

  if (onState) {
    if (!stateHandlersByInstance.has(instanceId)) {
      stateHandlersByInstance.set(instanceId, new Set());
    }
    stateHandlersByInstance.get(instanceId).add(onState);
  }

  await connectWebSocket();
  send('instance:subscribe', { instanceId });

  return () => {
    const set = handlersByInstance.get(instanceId);
    set?.delete(handler);
    if (set && set.size === 0) {
      handlersByInstance.delete(instanceId);
      try { send('instance:unsubscribe', { instanceId }); } catch {}
    }
    if (onState) {
      const sset = stateHandlersByInstance.get(instanceId);
      sset?.delete(onState);
      if (sset && sset.size === 0) stateHandlersByInstance.delete(instanceId);
    }
  };
}

// DEV ONLY (local server). One-click quick match vs the other connected
// test account. The server 404s this route in production.
export async function devQuickMatch(deckId) {
  return api.post('/instances/dev-quick-match', { deckId });
}

export function onDevQuickMatch(callback) {
  return on('dev:quickMatch', (data) => callback(data));
}
